"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ShoppingBag, Heart, ShoppingCart, ChevronRight } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useCart } from "@/lib/cart-context"
import { ImageWithFallback } from "@/components/ui/image-with-fallback"
import { useEffect, useState } from "react"
import { createClient } from "@/lib/supabase/client"
import { Skeleton } from "@/components/ui/skeleton"
import { useLanguage } from "@/lib/language-context"

interface DashboardGalleriesProps {
    user: any
}

export function DashboardGalleries({ user }: DashboardGalleriesProps) {
    const router = useRouter()
    const { t } = useLanguage()
    const { cart, wishlist } = useCart()
    const [orders, setOrders] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const supabase = createClient()

    useEffect(() => {
        if (user) {
            fetchOrders()
        }
    }, [user])

    const fetchOrders = async () => {
        try {
            const { data, error } = await supabase
                .from("orders")
                .select(`
                    id,
                    order_id,
                    status,
                    created_at,
                    total_amount,
                    order_items (
                        products (
                            image_url
                        )
                    )
                `)
                .eq("user_id", user.id)
                .order("created_at", { ascending: false })
                .limit(5)

            if (error) {
                console.error("Error fetching dashboard orders:", error)
            } else {
                setOrders(data || [])
            }
        } catch (error) {
            console.error("Failed to fetch dashboard orders", error)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="space-y-6">
            {/* My Orders */}
            <Card
                className="group relative overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 bg-white cursor-pointer"
                onClick={() => router.push('/orders')}
            >
                <div className="absolute top-0 left-0 w-1 h-full bg-primary/20 group-hover:bg-primary transition-colors"></div>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary transition-transform">
                            <ShoppingBag className="h-6 w-6" />
                        </div>
                        <div>
                            <CardTitle className="text-xl font-black text-gray-900 tracking-tight">{t("account.my_orders")}</CardTitle>
                            <p className="text-xs font-medium text-gray-500">{t("account.recent_activity") || "Track your recent purchases"}</p>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" className="text-xs font-bold text-primary hover:bg-primary/5 transition-all flex-shrink-0 ml-2">
                        {t("account.view_all")} <ChevronRight className="ml-1 h-3 w-3" />
                    </Button>
                </CardHeader>
                <CardContent className="pt-2 pb-6 min-w-0">
                    {isLoading ? (
                        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide min-w-0">
                            {[1, 2, 3].map((i) => (
                                <Skeleton key={i} className="min-w-[120px] h-36 rounded-2xl bg-gray-100 flex-shrink-0" />
                            ))}
                        </div>
                    ) : orders.length > 0 ? (
                        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide px-0.5 min-w-0">
                            {orders.map((order) => (
                                <div key={order.id} className="min-w-[120px] h-36 bg-gray-50/50 border border-gray-100 rounded-2xl flex items-center justify-center relative overflow-hidden p-3 hover:border-primary/30 hover:bg-white transition-all duration-300 flex-shrink-0">
                                    <div className="flex gap-2 justify-center w-full h-full items-center">
                                        {order.order_items?.slice(0, 2).map((item: any, idx: number) => (
                                            <div key={idx} className="relative w-20 h-20 rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100">
                                                <ImageWithFallback
                                                    src={item.products?.image_url || "/placeholder.svg"}
                                                    alt="Order Item"
                                                    fill
                                                    className="object-contain p-1 transform group-hover:scale-110 transition-transform duration-500"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="absolute top-2 right-2">
                                        <span className="h-2 w-2 rounded-full bg-primary animate-pulse"></span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center bg-gray-50/50 rounded-3xl border border-dashed border-gray-200 group-hover:bg-white transition-colors duration-500">
                            <div className="h-16 w-16 rounded-3xl bg-gray-100 flex items-center justify-center mb-4 transition-transform">
                                <ShoppingBag className="h-8 w-8 text-gray-300" />
                            </div>
                            <h3 className="text-base font-black text-gray-900">{t("account.no_orders")}</h3>
                            <p className="text-xs font-medium text-gray-500 mt-1 max-w-[240px]">
                                {t("account.no_orders_msg")}
                            </p>
                            <Button variant="default" asChild className="mt-6 bg-primary hover:bg-primary/90 rounded-xl h-10 px-8 text-xs font-bold shadow-lg shadow-primary/20">
                                <Link href="/" onClick={(e) => e.stopPropagation()}>
                                    {t("account.start_shopping")}
                                </Link>
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="grid gap-6 md:grid-cols-2">
                {/* Wishlist */}
                <Link href="/wishlist" className="block group">
                    <Card className="h-full relative overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 bg-white">
                        <div className="absolute top-0 right-0 p-4">
                            <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 transition-transform">
                                <Heart className="h-5 w-5 fill-red-500" />
                            </div>
                        </div>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-xl font-black text-gray-900 tracking-tight">{t("account.wishlist")}</CardTitle>
                            <p className="text-xs font-medium text-gray-500">{wishlist.length} {t("nav.products")?.toLowerCase() || "items"}</p>
                        </CardHeader>
                        <CardContent className="pt-4 min-w-0">
                            {wishlist.length > 0 ? (
                                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide px-0.5 min-w-0">
                                    {wishlist.slice(0, 3).map((item) => (
                                        <div key={item.id} className="relative h-24 w-24 min-w-[96px] flex-shrink-0 rounded-2xl border border-gray-100 bg-gray-50/30 overflow-hidden group-hover:border-red-100 transition-colors">
                                            <ImageWithFallback
                                                src={item.image_url || "/placeholder.svg"}
                                                alt={item.name}
                                                fill
                                                className="object-contain p-2 transform group-hover:scale-110 transition-transform duration-500"
                                            />
                                        </div>
                                    ))}
                                    {wishlist.length > 3 && (
                                        <div className="h-24 w-24 min-w-[96px] rounded-2xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-xs font-black text-gray-400">
                                            +{wishlist.length - 3}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-xs font-medium text-gray-400 py-6">{t("account.wishlist_empty")}</p>
                            )}
                        </CardContent>
                    </Card>
                </Link>

                {/* Cart */}
                <Link href="/cart" className="block group">
                    <Card className="h-full relative overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-500 bg-white">
                        <div className="absolute top-0 right-0 p-4">
                            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary transition-transform">
                                <ShoppingCart className="h-5 w-5" />
                            </div>
                        </div>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-xl font-black text-gray-900 tracking-tight">{t("account.my_cart")}</CardTitle>
                            <p className="text-xs font-medium text-gray-500">{cart.length} {t("nav.products")?.toLowerCase() || "items"}</p>
                        </CardHeader>
                        <CardContent className="pt-4 min-w-0">
                            {cart.length > 0 ? (
                                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide px-0.5 min-w-0">
                                    {cart.slice(0, 3).map((item) => (
                                        <div key={item.id} className="relative h-24 w-24 min-w-[96px] flex-shrink-0 rounded-2xl border border-gray-100 bg-gray-50/30 overflow-hidden group-hover:border-primary/20 transition-colors">
                                            <ImageWithFallback
                                                src={item.image_url || "/placeholder.svg"}
                                                alt={item.name}
                                                fill
                                                className="object-contain p-2 transform group-hover:scale-110 transition-transform duration-500"
                                            />
                                            <span className="absolute bottom-1 right-1 bg-primary text-white text-[8px] font-black px-1.5 py-0.5 rounded-lg shadow-sm">
                                                x{item.quantity}
                                            </span>
                                        </div>
                                    ))}
                                    {cart.length > 3 && (
                                        <div className="h-24 w-24 min-w-[96px] rounded-2xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-xs font-black text-gray-400">
                                            +{cart.length - 3}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-xs font-medium text-gray-400 py-6">{t("account.cart_empty")}</p>
                            )}
                        </CardContent>
                    </Card>
                </Link>
            </div>
        </div>
    )
}
